/**
 * Monthly housing payment display — aligned with pro-api loan mapping (GET /loans/{id}).
 * Prefer loan.totalMonthlyPayment; do not assume it equals the sum of P&I + escrows.
 * Fallback to summing components only when API total is null or zero (see PR #93).
 */

/** @param {Record<string, unknown> | null | undefined} loan */
export function sumMonthlyHousingComponents(loan) {
  if (!loan) return null;
  const parts = [
    loan.monthlyPrincipalAndInterest,
    loan.monthlyTaxes,
    loan.monthlyInsurance,
    loan.monthlyMortgageInsurance,
    loan.monthlyHoaDues,
  ].map((v) =>
    v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : null,
  );
  if (parts.every((x) => x == null)) return null;
  return parts.reduce((s, x) => s + (x ?? 0), 0);
}

/**
 * Value to show for total monthly housing payment (for fmtDollar).
 * @returns {number | null} null means show "—"
 */
export function getDisplayTotalMonthlyPayment(loan) {
  const raw = loan?.totalMonthlyPayment;
  const apiNum = raw != null && raw !== '' ? Number(raw) : null;
  const sum = sumMonthlyHousingComponents(loan);

  if (apiNum != null && !Number.isNaN(apiNum) && apiNum !== 0) {
    return apiNum;
  }
  if (sum != null && sum > 0) {
    return sum;
  }
  if (apiNum != null && !Number.isNaN(apiNum)) {
    return apiNum;
  }
  return null;
}

/**
 * Cosmetic label for the single MI field (loan.monthlyMortgageInsurance) by product type.
 * @param {string | undefined} mortgageType
 */
export function monthlyMortgageInsuranceLineLabel(mortgageType) {
  switch (mortgageType) {
    case 'FHA':
      return 'MIP';
    case 'USDA':
      return 'Annual fee';
    case 'VA':
      return 'Mortgage insurance';
    default:
      return 'MI';
  }
}
